// =====================================================
// FORMULARIO DE MOVIMIENTO AGRÍCOLA
// src/pages/Agro/StockAgricola/FormMovimientoAgricola.js
// =====================================================

import React, { useState, useEffect } from "react"
import { useFormik } from "formik"
import * as Yup from "yup"
import {
  Row,
  Col,
  Card,
  CardBody,
  Form,
  FormGroup,
  Label,
  Input,
  Button,
  Spinner,
  FormFeedback,
  InputGroup,
  InputGroupText,
} from "reactstrap"
import Flatpickr from "react-flatpickr"
import { Spanish } from "flatpickr/dist/l10n/es"
import dayjs from "dayjs"

import {
  getCultivos,
  getTiposAlmacenamiento,
  getCampanas,
  getPrecioGrano,
  saveMovimientoAgricola,
} from "api/agro"
import toastHandler from "toastHandler"

const TIPOS_MOVIMIENTO = [
  { value: "INGRESO", label: "Ingreso", color: "success", icon: "arrow-down" },
  { value: "EGRESO", label: "Egreso", color: "danger", icon: "arrow-up" },
]

const MOTIVOS = {
  INGRESO: [
    { value: "COSECHA", label: "Cosecha" },
    { value: "COMPRA", label: "Compra" },
    { value: "TRASLADO_INGRESO", label: "Traslado (Ingreso)" },
    { value: "OTRO_INGRESO", label: "Otro" },
  ],
  EGRESO: [
    { value: "VENTA", label: "Venta" },
    { value: "CONSUMO", label: "Consumo" },
    { value: "MERMA", label: "Merma" },
    { value: "TRASLADO_EGRESO", label: "Traslado (Egreso)" },
    { value: "OTRO_EGRESO", label: "Otro" },
  ],
}

const MOTIVOS_ECONOMICOS = ["COMPRA", "VENTA"]

const FormMovimientoAgricola = ({
  idEstablecimiento,
  establecimientos = [],
  onSuccess,
  onCancel,
}) => {
  const { setError, setSuccess, setWarnings } = toastHandler()
  const [loading, setLoading] = useState(false)
  const [cultivos, setCultivos] = useState([])
  const [tiposAlmacenamiento, setTiposAlmacenamiento] = useState([])
  const [campanas, setCampanas] = useState([])

  useEffect(() => {
    Promise.all([getCultivos(), getTiposAlmacenamiento(), getCampanas()])
      .then(([cultivosData, tiposData, campanasData]) => {
        setCultivos(cultivosData)
        setTiposAlmacenamiento(tiposData)
        setCampanas(campanasData)
      })
      .catch(err => setError(err))
  }, [])

  const formik = useFormik({
    initialValues: {
      idEstablecimiento: idEstablecimiento || "",
      fechaMovimiento: new Date(),
      tipoMovimiento: "INGRESO",
      motivo: "COSECHA",
      codigoCultivo: "",
      codigoTipoAlmacenamiento: "",
      cantidadQq: "",
      lote: "",
      campania: "",
      numeroDte: "",
      precioQq: "",
      importeTotal: "",
      moneda: "USD",
      establecimientoContraparte: "",
    },
    validationSchema: Yup.object({
      idEstablecimiento: Yup.number().required("Establecimiento requerido"),
      fechaMovimiento: Yup.date()
        .required("Fecha requerida")
        .max(new Date(), "No puede ser futura"),
      tipoMovimiento: Yup.string().required("Tipo requerido"),
      motivo: Yup.string().required("Motivo requerido"),
      codigoCultivo: Yup.string().required("Cultivo requerido"),
      cantidadQq: Yup.number()
        .min(0.01, "Mínimo 0.01 QQ")
        .required("Cantidad requerida"),
    }),
    onSubmit: async values => {
      setLoading(true)
      try {
        const data = {
          idEstablecimiento: parseInt(values.idEstablecimiento),
          fechaMovimiento: dayjs(values.fechaMovimiento).format("YYYY-MM-DD"),
          tipoMovimiento: values.tipoMovimiento,
          motivo: values.motivo,
          codigoCultivo: values.codigoCultivo,
          codigoTipoAlmacenamiento: values.codigoTipoAlmacenamiento || null,
          cantidadQq: parseFloat(values.cantidadQq),
          lote: values.lote || null,
          campania: values.campania || null,
          numeroDte: values.numeroDte || null,
          establecimientoContraparte: values.establecimientoContraparte || null,
        }

        if (MOTIVOS_ECONOMICOS.includes(values.motivo)) {
          data.precioQq = values.precioQq ? parseFloat(values.precioQq) : null
          data.importeTotal = values.importeTotal
            ? parseFloat(values.importeTotal)
            : null
          data.moneda = values.moneda
        }

        const result = await saveMovimientoAgricola(data)
        if (result.stockActualizado === false) {
          setWarnings(
            "El movimiento se registró, pero no se pudo actualizar el stock automáticamente. Revisá el stock de este establecimiento/cultivo manualmente.",
          )
        } else {
          setSuccess("Movimiento agrícola registrado correctamente")
        }
        onSuccess && onSuccess()
      } catch (err) {
        setError(err)
      } finally {
        setLoading(false)
      }
    },
  })

  const { values, errors, touched, handleChange, handleSubmit, setFieldValue } =
    formik

  // Cambiar tipo de movimiento resetea el motivo
  const handleTipoChange = e => {
    const nuevoTipo = e.target.value
    setFieldValue("tipoMovimiento", nuevoTipo)
    const nuevosMotivos = MOTIVOS[nuevoTipo]
    if (nuevosMotivos && nuevosMotivos.length > 0) {
      setFieldValue("motivo", nuevosMotivos[0].value)
    }
  }

  // Al elegir cultivo (o cambiar a un motivo economico) con Compra/Venta,
  // autocompletar el precio por QQ desde precio_grano si el usuario todavia
  // no cargo uno a mano. Esto dispara el calculo automatico del Importe Total.
  useEffect(() => {
    if (
      !MOTIVOS_ECONOMICOS.includes(values.motivo) ||
      !values.codigoCultivo ||
      values.precioQq
    ) {
      return
    }
    let cancelado = false
    getPrecioGrano(values.codigoCultivo)
      .then(precio => {
        if (cancelado || !precio?.precioUsdTn) return
        const precioQqUsd = Number(precio.precioUsdTn) / 10
        setFieldValue("precioQq", precioQqUsd.toFixed(2))
      })
      .catch(() => {
        // Sin precio de referencia disponible: el usuario lo carga a mano.
      })
    return () => {
      cancelado = true
    }
  }, [values.codigoCultivo, values.motivo])

  // Calcular importe total: cantidadQq × precioQq
  useEffect(() => {
    if (MOTIVOS_ECONOMICOS.includes(values.motivo)) {
      const cantidad = parseFloat(values.cantidadQq) || 0
      const precioQq = parseFloat(values.precioQq) || 0

      if (cantidad > 0 && precioQq > 0) {
        setFieldValue("importeTotal", (cantidad * precioQq).toFixed(2))
      }
    }
  }, [values.cantidadQq, values.precioQq, values.motivo])

  const mostrarDatosEconomicos = MOTIVOS_ECONOMICOS.includes(values.motivo)

  return (
    <Card>
      <CardBody>
        <h5 className="card-title mb-4">
          <i className="mdi mdi-swap-vertical me-2" />
          Registrar Movimiento Agrícola
        </h5>

        <Form onSubmit={handleSubmit}>
          {/* Tipo de Movimiento */}
          <Row className="mb-4">
            <Col md={12}>
              <Label className="d-block mb-2">Tipo de Movimiento *</Label>
              <div className="d-flex gap-3">
                {TIPOS_MOVIMIENTO.map(tipo => (
                  <div
                    key={tipo.value}
                    className="form-check form-check-inline"
                  >
                    <Input
                      type="radio"
                      id={`tipo-${tipo.value}`}
                      name="tipoMovimiento"
                      value={tipo.value}
                      checked={values.tipoMovimiento === tipo.value}
                      onChange={handleTipoChange}
                      className="form-check-input"
                    />
                    <Label
                      className="form-check-label"
                      htmlFor={`tipo-${tipo.value}`}
                    >
                      <i
                        className={`mdi mdi-${tipo.icon}-bold text-${tipo.color} me-1`}
                      />
                      {tipo.label}
                    </Label>
                  </div>
                ))}
              </div>
            </Col>
          </Row>

          <Row>
            <Col md={6}>
              <FormGroup>
                <Label>Establecimiento *</Label>
                <Input
                  type="select"
                  name="idEstablecimiento"
                  value={values.idEstablecimiento}
                  onChange={handleChange}
                  invalid={
                    touched.idEstablecimiento && !!errors.idEstablecimiento
                  }
                >
                  <option value="">Seleccione...</option>
                  {establecimientos.map(est => (
                    <option key={est.id} value={est.id}>
                      {est.nombre}
                    </option>
                  ))}
                </Input>
                <FormFeedback>{errors.idEstablecimiento}</FormFeedback>
              </FormGroup>
            </Col>

            <Col md={3}>
              <FormGroup>
                <Label>Fecha de Movimiento *</Label>
                <Flatpickr
                  className="form-control"
                  value={values.fechaMovimiento}
                  onChange={([date]) => setFieldValue("fechaMovimiento", date)}
                  options={{
                    dateFormat: "d/m/Y",
                    locale: Spanish,
                    maxDate: "today",
                  }}
                />
                {touched.fechaMovimiento && errors.fechaMovimiento && (
                  <div className="text-danger small mt-1">
                    {errors.fechaMovimiento}
                  </div>
                )}
              </FormGroup>
            </Col>

            <Col md={3}>
              <FormGroup>
                <Label>Nro. DTE/Guía</Label>
                <Input
                  type="text"
                  name="numeroDte"
                  value={values.numeroDte}
                  onChange={handleChange}
                  placeholder="Opcional"
                />
              </FormGroup>
            </Col>
          </Row>

          <Row>
            <Col md={4}>
              <FormGroup>
                <Label>Motivo *</Label>
                <Input
                  type="select"
                  name="motivo"
                  value={values.motivo}
                  onChange={handleChange}
                  invalid={touched.motivo && !!errors.motivo}
                >
                  {MOTIVOS[values.tipoMovimiento].map(motivo => (
                    <option key={motivo.value} value={motivo.value}>
                      {motivo.label}
                    </option>
                  ))}
                </Input>
                <FormFeedback>{errors.motivo}</FormFeedback>
              </FormGroup>
            </Col>

            <Col md={4}>
              <FormGroup>
                <Label>Cultivo *</Label>
                <Input
                  type="select"
                  name="codigoCultivo"
                  value={values.codigoCultivo}
                  onChange={handleChange}
                  invalid={touched.codigoCultivo && !!errors.codigoCultivo}
                >
                  <option value="">Seleccione...</option>
                  {cultivos.map(c => (
                    <option key={c.codigo} value={c.codigo}>
                      {c.nombre}
                    </option>
                  ))}
                </Input>
                <FormFeedback>{errors.codigoCultivo}</FormFeedback>
              </FormGroup>
            </Col>

            <Col md={4}>
              <FormGroup>
                <Label>Tipo Almacenamiento</Label>
                <Input
                  type="select"
                  name="codigoTipoAlmacenamiento"
                  value={values.codigoTipoAlmacenamiento}
                  onChange={handleChange}
                >
                  <option value="">Sin tipo</option>
                  {tiposAlmacenamiento.map(t => (
                    <option key={t.codigo} value={t.codigo}>
                      {t.descripcion}
                    </option>
                  ))}
                </Input>
              </FormGroup>
            </Col>
          </Row>

          <Row>
            <Col md={4}>
              <FormGroup>
                <Label>Cantidad (QQ) *</Label>
                <Input
                  type="number"
                  step="0.01"
                  name="cantidadQq"
                  value={values.cantidadQq}
                  onChange={handleChange}
                  invalid={touched.cantidadQq && !!errors.cantidadQq}
                  placeholder="Ej: 500"
                />
                <FormFeedback>{errors.cantidadQq}</FormFeedback>
              </FormGroup>
            </Col>

            <Col md={4}>
              <FormGroup>
                <Label>Lote</Label>
                <Input
                  type="text"
                  name="lote"
                  value={values.lote}
                  onChange={handleChange}
                  placeholder="Opcional"
                />
              </FormGroup>
            </Col>

            <Col md={4}>
              <FormGroup>
                <Label>Campaña</Label>
                <Input
                  type="select"
                  name="campania"
                  value={values.campania}
                  onChange={handleChange}
                >
                  <option value="">Seleccione...</option>
                  {campanas.map(c => (
                    <option key={c.codigo} value={c.codigo}>
                      {c.codigo}
                    </option>
                  ))}
                </Input>
              </FormGroup>
            </Col>
          </Row>

          {/* Datos Económicos (solo COMPRA/VENTA) */}
          {mostrarDatosEconomicos && (
            <>
              <hr className="my-4" />
              <h6 className="mb-3">
                <i className="mdi mdi-cash me-2" />
                Datos Económicos
              </h6>

              <Row>
                <Col md={4}>
                  <FormGroup>
                    <Label>Precio por QQ</Label>
                    <InputGroup>
                      <InputGroupText>$</InputGroupText>
                      <Input
                        type="number"
                        step="0.01"
                        name="precioQq"
                        value={values.precioQq}
                        onChange={handleChange}
                        placeholder="0.00"
                      />
                    </InputGroup>
                    <small className="text-muted">1 QQ = 100 kg</small>
                  </FormGroup>
                </Col>

                <Col md={4}>
                  <FormGroup>
                    <Label>Moneda</Label>
                    <Input
                      type="select"
                      name="moneda"
                      value={values.moneda}
                      onChange={handleChange}
                    >
                      <option value="USD">USD</option>
                      <option value="ARS">ARS</option>
                    </Input>
                  </FormGroup>
                </Col>

                <Col md={4}>
                  <FormGroup>
                    <Label>Importe Total</Label>
                    <InputGroup>
                      <InputGroupText>$</InputGroupText>
                      <Input
                        type="number"
                        step="0.01"
                        name="importeTotal"
                        value={values.importeTotal}
                        onChange={handleChange}
                        placeholder="Auto"
                      />
                    </InputGroup>
                  </FormGroup>
                </Col>
              </Row>
            </>
          )}

          {/* Botones */}
          <div className="d-flex justify-content-end gap-2 mt-4">
            <Button color="secondary" onClick={onCancel} disabled={loading}>
              Cancelar
            </Button>
            <Button color="primary" type="submit" disabled={loading}>
              {loading ? (
                <>
                  <Spinner size="sm" className="me-2" />
                  Guardando...
                </>
              ) : (
                <>
                  <i className="mdi mdi-content-save me-1" />
                  Registrar Movimiento
                </>
              )}
            </Button>
          </div>
        </Form>
      </CardBody>
    </Card>
  )
}

export default FormMovimientoAgricola
